import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { 
  ArrowLeft, 
  ChevronRight,
  Shield,
  Bell,
  BellRing,
  Lock,
  Fingerprint,
  HelpCircle,
  MessageSquare,
  FileText,
  LogOut,
  CheckCircle2,
  Edit3,
  Smartphone,
  Crown,
  Gift,
  Repeat,
  TrendingUp,
  Handshake,
  Clock,
  XCircle
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const Settings = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [premiumStatus, setPremiumStatus] = useState(null);
  const [partnerStatus, setPartnerStatus] = useState(null);

  // Fetch premium status and partner status
  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const token = localStorage.getItem("stelx_token");
        const headers = { Authorization: `Bearer ${token}` };
        
        const [premiumRes, partnerRes] = await Promise.all([
          axios.get(`${API}/premium/status`, { headers }),
          axios.get(`${API}/promoter/application/status`, { headers }).catch(() => ({ data: null }))
        ]);
        
        setPremiumStatus(premiumRes.data);
        setPartnerStatus(partnerRes.data);
      } catch (error) {
        console.error("Error fetching statuses:", error);
      }
    };
    fetchStatuses();
  }, []);

  // Fetch health score for verification status hint
  const { data: healthScore } = useQuery({
    queryKey: ["healthScore"],
    queryFn: async () => {
      const response = await axios.get(`${API}/ai/health-score`);
      return response.data;
    }
  });

  const handleLogout = () => {
    localStorage.removeItem("stelx_token");
    toast.success("Signed out successfully");
    if (onLogout) onLogout();
    navigate("/");
  };

  const handleBiometricToggle = (enabled) => {
    setBiometricEnabled(enabled);
    toast.success(enabled ? "Biometric login enabled" : "Biometric login disabled");
  };

  // Check if user is verified (mock based on health score)
  const isVerified = (healthScore?.score || 0) >= 75;

  const NavRow = ({ icon: Icon, label, subtitle, onClick, danger }) => (
    <button
      onClick={onClick}
      className={`w-full flex items-center justify-between py-4 px-4 border-b border-white/5 last:border-0
        hover:bg-white/5 transition-colors text-left`}
    >
      <div className="flex items-center gap-4">
        <div className={`w-10 h-10 rounded-xl ${danger ? "bg-red-500/20" : "bg-white/5"} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-5 h-5 ${danger ? "text-red-400" : "text-muted-foreground"}`} />
        </div>
        <div className="min-w-0">
          <p className={`text-sm font-medium ${danger ? "text-red-400" : "text-white"}`}>{label}</p>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        </div>
      </div>
      <ChevronRight className={`w-5 h-5 flex-shrink-0 ${danger ? "text-red-400" : "text-muted-foreground"}`} />
    </button>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="settings-page">
      {/* Header */}
      <header className="px-5 pt-4 pb-2 flex items-center gap-4 max-w-lg mx-auto">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/dashboard")}
          className="text-white hover:bg-white/10"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="font-heading font-semibold text-xl text-white">Settings</h1>
      </header>

      <main className="px-5 pb-24 max-w-lg mx-auto space-y-6">
        {/* Profile Header */}
        <div className="glass rounded-3xl p-5">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-white/10 border border-white/20 overflow-hidden flex items-center justify-center">
              {user?.picture ? (
                <img src={user.picture} alt={user?.name} className="w-full h-full object-cover" />
              ) : (
                <span className="text-2xl font-semibold text-white">
                  {user?.name?.charAt(0) || "U"}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="font-heading font-semibold text-lg text-white truncate">
                  {user?.name || "User"}
                </h2>
                {isVerified && (
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                )}
              </div>
              <p className="text-sm text-muted-foreground truncate">
                {user?.email || "user@stelxpay.com"}
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="text-muted-foreground hover:text-white hover:bg-white/10 flex-shrink-0"
              onClick={() => toast.info("Edit profile coming soon")}
              data-testid="edit-profile-btn"
            >
              <Edit3 className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Verification Prompt */}
        {!isVerified && (
          <div className="glass rounded-3xl p-5 border-pink-500/30 bg-gradient-to-br from-pink-500/10 to-transparent">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl gradient-primary flex items-center justify-center flex-shrink-0">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-white mb-1">Complete Verification</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Unlock higher limits and features by verifying your identity.
                </p>
                <Button
                  className="btn-primary h-9 px-4 text-sm"
                  onClick={() => navigate("/settings/kyc")}
                  data-testid="verify-now-btn"
                >
                  Verify Now
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Account Section */}
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3 px-1">
            Account
          </p>
          <div className="glass rounded-2xl overflow-hidden">
            {/* Biometric Login */}
            <div className="flex items-center justify-between px-4 py-4 border-b border-white/5">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center flex-shrink-0">
                  <Fingerprint className="w-5 h-5 text-muted-foreground" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-white">Biometric Login</p>
                  <p className="text-xs text-muted-foreground">Use Face ID or fingerprint</p>
                </div>
              </div>
              <Switch
                checked={biometricEnabled}
                onCheckedChange={handleBiometricToggle}
              />
            </div>

            <NavRow
              icon={Lock}
              label="Change PIN"
              subtitle="Update your security PIN"
              onClick={() => toast.info("PIN change coming soon")}
            />
            <NavRow
              icon={Bell}
              label="Notifications"
              subtitle="Alerts and preferences"
              onClick={() => navigate("/settings/notifications")}
            />
            <NavRow
              icon={BellRing}
              label="Push Notifications"
              subtitle="Background alerts via Firebase"
              onClick={() => navigate("/settings/push")}
            />
            <NavRow
              icon={Smartphone}
              label="SMS Alerts"
              subtitle="Remittance & transaction SMS"
              onClick={() => navigate("/settings/sms")}
            />
            <NavRow
              icon={Shield}
              label="Privacy & Security"
              subtitle="Manage your data and security"
              onClick={() => toast.info("Privacy settings coming soon")}
            />
          </div>
        </div>

        {/* Premium & Features Section */}
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3 px-1">
            Premium & Features
          </p>
          <div className="glass rounded-2xl overflow-hidden">
            {/* Premium Status */}
            <button
              onClick={() => navigate("/premium")}
              className="w-full flex items-center justify-between py-4 px-4 border-b border-white/5 hover:bg-white/5 transition-colors text-left"
            >
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-xl ${premiumStatus?.is_premium ? "bg-gradient-to-br from-amber-500 to-orange-500" : "bg-white/5"} flex items-center justify-center`}>
                  <Crown className={`w-5 h-5 ${premiumStatus?.is_premium ? "text-white" : "text-muted-foreground"}`} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-white">Stelx Premium</p>
                    {premiumStatus?.is_premium && (
                      <span className="px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-400 text-[10px] font-medium">
                        Active
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {premiumStatus?.is_premium ? "Manage your subscription" : "Unlock premium features"}
                  </p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </button>

            <NavRow
              icon={Gift}
              label="Refer & Earn"
              subtitle="Invite friends, get J$1,500 each"
              onClick={() => navigate("/referral")}
            />
            <NavRow
              icon={Repeat}
              label="Recurring Payments"
              subtitle="Manage scheduled transfers"
              onClick={() => navigate("/recurring")}
            />
            <NavRow
              icon={TrendingUp}
              label="Analytics"
              subtitle="Spending insights & reports"
              onClick={() => navigate("/analytics")}
            />
          </div>
        </div>

        {/* Partner Section */}
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3 px-1">
            Partner with Stelx
          </p>
          <div className="glass rounded-2xl overflow-hidden">
            {/* Partner Status Card */}
            <button
              onClick={() => {
                if (partnerStatus?.status === "verified") {
                  navigate("/promoter/dashboard");
                } else {
                  navigate("/partner/apply");
                }
              }}
              className="w-full p-4 text-left hover:bg-white/5 transition-colors"
            >
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  partnerStatus?.status === "verified" 
                    ? "bg-gradient-to-br from-teal-500 to-cyan-600" 
                    : partnerStatus?.status === "pending"
                      ? "bg-amber-500/20"
                      : partnerStatus?.status === "rejected"
                        ? "bg-red-500/20"
                        : "bg-white/5"
                }`}>
                  {partnerStatus?.status === "verified" ? (
                    <CheckCircle2 className="w-6 h-6 text-white" />
                  ) : partnerStatus?.status === "pending" ? (
                    <Clock className="w-6 h-6 text-amber-400" />
                  ) : partnerStatus?.status === "rejected" ? (
                    <XCircle className="w-6 h-6 text-red-400" />
                  ) : (
                    <Handshake className="w-6 h-6 text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-medium text-white">
                      {partnerStatus?.status === "verified" 
                        ? "Partner Dashboard" 
                        : partnerStatus?.status === "pending"
                          ? "Application Pending"
                          : partnerStatus?.status === "rejected"
                            ? "Application Not Approved"
                            : "Become a Partner"}
                    </p>
                    {partnerStatus?.status === "verified" && (
                      <span className="px-1.5 py-0.5 rounded-full bg-teal-500/20 text-teal-400 text-[10px] font-medium">
                        Verified
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {partnerStatus?.status === "verified" 
                      ? "Manage your events, track sales, and view earnings"
                      : partnerStatus?.status === "pending"
                        ? "We're reviewing your application. You'll be notified once approved."
                        : partnerStatus?.status === "rejected"
                          ? "Please contact support or reapply with updated documents."
                          : "List your events, set prices, and let patrons pay & secure tickets with ease."}
                  </p>
                  {!partnerStatus?.status && (
                    <div className="mt-3 pt-3 border-t border-white/5">
                      <p className="text-[11px] text-muted-foreground">
                        <span className="text-primary font-medium">Service Fee:</span> 3.5% - 6% per ticket sold (based on price tier). 
                        You'll see exactly what you earn before listing.
                      </p>
                    </div>
                  )}
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-1" />
              </div>
            </button>
          </div>
        </div>

        {/* Support Section */}
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3 px-1">
            Support
          </p>
          <div className="glass rounded-2xl overflow-hidden">
            <NavRow
              icon={HelpCircle}
              label="Help Center"
              subtitle="FAQs and guides"
              onClick={() => toast.info("Help Center coming soon")}
            />
            <NavRow
              icon={MessageSquare}
              label="Contact Support"
              subtitle="Get help from our team"
              onClick={() => toast.info("Contact Support coming soon")}
            />
          </div>
        </div>

        {/* Legal Section */}
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3 px-1">
            Legal
          </p>
          <div className="glass rounded-2xl overflow-hidden">
            <NavRow
              icon={FileText}
              label="Terms of Service"
              onClick={() => toast.info("Terms of Service coming soon")}
            />
            <NavRow
              icon={FileText}
              label="Privacy Policy"
              onClick={() => toast.info("Privacy Policy coming soon")}
            />
          </div>
        </div>

        {/* Sign Out */}
        <button
          onClick={handleLogout}
          className="w-full glass rounded-2xl p-4 flex items-center justify-center gap-3 hover:bg-red-500/10 transition-colors border-red-500/20"
          data-testid="sign-out-btn"
        >
          <LogOut className="w-5 h-5 text-red-400" />
          <span className="text-sm font-medium text-red-400">Sign Out</span>
        </button>

        {/* Version */}
        <p className="text-xs text-muted-foreground/50 text-center">
          Stelx Pay v1.0.0
        </p>
      </main>
    </div>
  );
};

export default Settings;
